import api from "./api";

export type YoutubeAdminAccount = {
  id: number;
  name: string;
  scheduleLimit: number;
  active: boolean;
  displayOrder: number;
  tokenStatus: string;
  requiresReconnect: boolean;
  channelId: string | null;
  channelName: string | null;
  channelImageUrl: string | null;
  lastSyncedAt: string | null;
  activeScheduleCount: number;
  /** true quando a conta foi vinculada pelo próprio OWNER (self-service), não pelo pool administrativo. */
  ownerManaged: boolean;
  /** E-mail da conta Google que autorizou o OAuth (útil pra lembrar qual login pertence a qual canal). */
  connectedEmail: string | null;
};

const normalizeAccount = (raw: any): YoutubeAdminAccount => ({
  id: Number(raw?.id),
  name: String(raw?.name ?? ""),
  scheduleLimit: Number(raw?.scheduleLimit ?? 0),
  active: !!raw?.active,
  displayOrder: Number(raw?.displayOrder ?? 0),
  tokenStatus: raw?.tokenStatus ? String(raw.tokenStatus) : "DISCONNECTED",
  requiresReconnect: !!raw?.requiresReconnect,
  channelId: raw?.channelId ? String(raw.channelId) : null,
  channelName: raw?.channelName ? String(raw.channelName) : null,
  channelImageUrl: raw?.channelImageUrl ? String(raw.channelImageUrl) : null,
  lastSyncedAt: raw?.lastSyncedAt ? String(raw.lastSyncedAt) : null,
  activeScheduleCount: Number(raw?.activeScheduleCount ?? 0),
  ownerManaged: !!raw?.ownerManaged,
  connectedEmail: raw?.connectedEmail ? String(raw.connectedEmail) : null,
});

export const listYoutubeAdminAccounts = async (): Promise<YoutubeAdminAccount[]> => {
  const response = await api.get("/app/lives/youtube/admin-accounts");
  const rows = response.data?.data ?? response.data ?? [];
  return Array.isArray(rows) ? rows.map(normalizeAccount) : [];
};

export const createYoutubeAdminAccount = async (payload: {
  name: string;
  scheduleLimit?: number;
  active?: boolean;
}): Promise<YoutubeAdminAccount> => {
  const response = await api.post("/app/lives/youtube/admin-accounts", payload);
  return normalizeAccount(response.data?.data ?? response.data);
};

export const startYoutubeAdminAccountConnection = async (
  id: number,
  options?: { popup?: boolean }
): Promise<{ authorizationUrl?: string | null }> => {
  const browserReturnUrl = typeof window !== "undefined" ? window.location.href : undefined;
  const response = await api.post(`/app/lives/youtube/admin-accounts/${id}/start`, {
    ...(browserReturnUrl ? { browserReturnUrl } : {}),
    ...(options?.popup ? { popup: true } : {}),
  });
  const payload = response.data?.data ?? response.data ?? {};
  return {
    authorizationUrl: payload?.authorizationUrl ? String(payload.authorizationUrl) : null,
  };
};

export const updateYoutubeAdminAccount = async (
  id: number,
  payload: { name?: string; scheduleLimit?: number; active?: boolean; displayOrder?: number }
): Promise<YoutubeAdminAccount> => {
  const response = await api.patch(`/app/lives/youtube/admin-accounts/${id}`, payload);
  return normalizeAccount(response.data?.data ?? response.data);
};

export const unlinkYoutubeAdminAccount = async (id: number): Promise<void> => {
  await api.post(`/app/lives/youtube/admin-accounts/${id}/unlink`);
};

export const deleteYoutubeAdminAccount = async (id: number): Promise<void> => {
  await api.delete(`/app/lives/youtube/admin-accounts/${id}`);
};
